import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { BusinessCenter, AdminPanelSettings, Visibility, VisibilityOff } from '@mui/icons-material';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [ruc, setRuc] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated, user } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // Calcular si el formulario es válido
  const isFormValid = ruc.trim().length === 11 && password.trim().length > 0;

  if (isAuthenticated) {
    if (user?.tipo === 'admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/comercio" replace />;
    }
  }

  const validateRUC = (ruc: string): boolean => {
    // Validación básica de RUC
    const rucRegex = /^\d{11}$/;
    return rucRegex.test(ruc);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!ruc.trim()) {
      setError('RUC es requerido');
      return;
    }

    if (!validateRUC(ruc)) {
      setError('RUC debe tener 11 dígitos');
      return;
    }

    if (!password.trim()) {
      setError('Password es requerido');
      return;
    }

    setIsLoading(true);

    try {
      // Obtener token de reCAPTCHA
      let recaptchaToken: string | undefined = undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('login');
        } catch (recaptchaError) {
          console.warn('Error obteniendo token reCAPTCHA:', recaptchaError);
          // Continuar sin reCAPTCHA en caso de error
        }
      }

      const success = await login(ruc, password, recaptchaToken);
      
      if (!success) {
        setError('RUC o password incorrecto');
      }
    } catch (error: any) {
      console.error('Error en login:', error);
      if (error.message?.includes('Verificación de seguridad')) {
        setError('Error de verificación de seguridad. Inténtelo nuevamente.');
      } else {
        setError('Error de conexión. Inténtelo nuevamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        {/* Logo/Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom color="primary">
            Mascotas365
          </Typography>
          <Typography variant="h5" color="text.secondary">
            Sistema de Cupones
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h5" component="h2" align="center" gutterBottom>
              Iniciar Sesión
            </Typography>
            
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Ingresa tu RUC y password para acceder al panel
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="RUC"
              value={ruc}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                setRuc(value);
                setError('');
              }}
              placeholder="12345678901"
              variant="outlined"
              margin="normal"
              required
              disabled={isLoading}
              helperText="Ingresa tu RUC de 11 dígitos"
              inputProps={{
                maxLength: 11,
                pattern: '[0-9]*',
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              variant="outlined"
              margin="normal"
              required
              disabled={isLoading}
              helperText="Clave provista por el administrador"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !isFormValid}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Ingresar'
              )}
            </Button>
          </Box>
        </Paper>

        {/* Información adicional */}
        <Box sx={{ mt: 4, width: '100%', maxWidth: 600 }}>
          <Typography variant="h6" align="center" gutterBottom>
            Tipos de Acceso
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Card sx={{ flex: 1 }}>
              <CardHeader 
                avatar={<BusinessCenter color="primary" />}
                title="Comercios"
                titleTypographyProps={{ variant: 'h6' }}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Acceso para comercios afiliados. Gestiona redenciones de cupones y visualiza estadísticas.
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardHeader 
                avatar={<AdminPanelSettings color="secondary" />}
                title="Administrador"
                titleTypographyProps={{ variant: 'h6' }}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Panel administrativo de Mascotas365. Gestión completa de cupones y comercios.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            ¿Problemas para acceder? Contacta al administrador
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
